import React, { useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import { Form, Formik } from "formik";

import { FieldGroup, SubmitButton, OTPVerification, Alert, OtpCaptcha } from "../../components";
import { Button } from "../../components/ui";
import useOtpCaptcha from "../../hooks/useOtpCaptcha";

// Stable target for the phone field's aria-describedby. The region is always
// rendered on the standalone step so the reference resolves even before a send
// has failed — a describedby pointing at nothing is announced as nothing.
const SEND_ERROR_ID = "rc-signin-phone-error";

// `standalone` is the /sign-in page only. AssignRequest embeds this same form
// inside its own card, where the legacy chrome still has to match the Register
// form beside it — so the presentation below stays split rather than migrated.
const RCLoginForm = ({ onOtpVerified, standalone = false }) => {
  const [stage, setStage] = useState("phone"); // phone | otp
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sentByEmail, setSentByEmail] = useState(false);
  const [error, setError] = useState(null);
  const { recaptchaRef, enabled: captchaEnabled, siteKey, withToken } = useOtpCaptcha();

  const phoneSchema = Yup.object({
    phone: Yup.string().required("Phone number is required."),
  });

  const handleSendOtp = async (data) => {
    setError(null);
    const phone = data.phone;

    try {
      const res = await axios.post("/api/send-otp", await withToken({ phoneNumber: phone }));
      // RC master admins get their code by email, not SMS.
      setSentByEmail(res?.data?.channel === "email");
      setPhoneNumber(phone);
      setStage("otp");
    } catch (err) {
      // The backend answers 429 with its own message and a retry_after when a
      // send budget is spent (too many codes for this number, or from this
      // device). Prefer that message — it tells the user how long to wait,
      // which a generic failure string cannot.
      const msg = err.response?.data?.message || "Failed to send OTP. Please try again.";
      setError(msg);
    }
  };

  const handleOtpVerified = async (otpCode) => {
    // Pass both phone and OTP code — let SignIn.jsx do single verification via signin-with-tokens
    await onOtpVerified(phoneNumber, otpCode);
  };

  if (stage === "otp") {
    return (
      <OTPVerification
        phoneNumber={phoneNumber}
        sentByEmail={sentByEmail}
        onVerified={handleOtpVerified}
        onBack={() => setStage("phone")}
      />
    );
  }

  const sendError = error && (
    <Alert type="error" message="Error" description={error} className="form-error" />
  );

  return (
    <div className="rc-signin-form">
      {standalone ? (
        <div id={SEND_ERROR_ID} role="status" aria-live="polite">
          {sendError}
        </div>
      ) : (
        sendError
      )}

      <Formik
        initialValues={{ phone: "" }}
        validationSchema={phoneSchema}
        onSubmit={handleSendOtp}
      >
        {({ isSubmitting }) => (
          <Form>
            <FieldGroup
              placeholder="Phone number"
              type="phone"
              name="phone"
              required
              {...(standalone
                ? {
                    label: "Phone number",
                    // FieldGroup already gives the input `id={name}` and the
                    // label `htmlFor={name}`; `field` is its existing pass-through
                    // for props it does not model itself.
                    field: {
                      required: true,
                      "aria-describedby": SEND_ERROR_ID,
                      "aria-invalid": error ? true : undefined,
                    },
                  }
                : {})}
            />

            {standalone ? (
              <Button
                type="submit"
                variant="primary"
                size="xl"
                fullWidth
                loading={isSubmitting}
                // Fontaine Button marks a loading control aria-disabled but
                // leaves it natively enabled; the legacy SubmitButton disabled
                // it. Keep that, or Enter in the field re-submits mid-send.
                disabled={isSubmitting}
                className="mt-4 mb-4"
              >
                Send OTP
              </Button>
            ) : (
              <div className="action-row mt-4 mb-4">
                <SubmitButton label="Send OTP" isSubmitting={isSubmitting} />
              </div>
            )}

            {/* Hidden badge + the attribution the reCAPTCHA terms require.
                Placed after the button so the notice reads as part of the form
                rather than floating over it — see components/OtpCaptcha.jsx. */}
            <OtpCaptcha
              innerRef={recaptchaRef}
              enabled={captchaEnabled}
              siteKey={siteKey}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RCLoginForm;
